import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, LifeBuoy, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Kaharian v3.0 "submission_uncertain" support modal.
 *
 * When the Hub can't confirm whether a paid generation was submitted to the provider,
 * it returns HTTP 202 with {status:"submission_uncertain", credits_charged:false, retryable:false}.
 *
 * We MUST NOT retry (double-charge risk) and we MUST surface the uncertainty clearly.
 * This modal gives the user a pre-filled support ticket with all the context support needs.
 */
export function SubmissionUncertainModal({ open, onClose, context }) {
  const [copied, setCopied] = useState(false);
  if (!context) return null;

  const { category, idempotencyKey, message, timestamp } = context;
  const supportEmail = 'support@kaharian.com';
  const ticketBody =
`Hi Kaharian support,

I ran into a "submission_uncertain" response from the Konnect Creative Studio.
Please confirm whether the generation was submitted and let me know if any refund is needed.

— Category: ${category || 'unknown'}
— Idempotency-Key: ${idempotencyKey || 'n/a'}
— Timestamp (UTC): ${timestamp || new Date().toISOString()}
— Hub message: ${message || 'Outcome unknown — credits not charged per v3.0 contract.'}

Thanks!`;
  const mailtoHref = `mailto:${supportEmail}?subject=${encodeURIComponent(
    `Konnect Studio — submission_uncertain (${category || 'unknown'})`
  )}&body=${encodeURIComponent(ticketBody)}`;

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(idempotencyKey || '');
      setCopied(true);
      toast.success('Idempotency key copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="submission-uncertain-modal">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base">Outcome unknown</DialogTitle>
              <DialogDescription className="text-xs mt-1 leading-relaxed">
                We couldn't confirm whether your {category ? category.toLowerCase() : 'generation'} was
                successfully submitted. <span className="font-semibold text-emerald-700">No credits were charged.</span> Do
                not click Generate again — this could cause a duplicate. Contact support with the reference below.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Reference card */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-2">
            <div>
              <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide">Reference ID</p>
              <div className="flex items-center gap-2 mt-0.5">
                <code className="text-[11px] font-mono text-zinc-800 break-all flex-1" data-testid="submission-uncertain-ref">
                  {idempotencyKey || '—'}
                </code>
                {idempotencyKey && (
                  <button
                    onClick={copyRef}
                    className="p-1.5 rounded-md hover:bg-zinc-200 text-zinc-500 hover:text-zinc-700 transition-colors"
                    title="Copy"
                    data-testid="submission-uncertain-copy-btn"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </div>
            {message && (
              <div>
                <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide">Hub message</p>
                <p className="text-xs text-zinc-700 mt-0.5">{message}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              asChild
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              data-testid="submission-uncertain-support-btn"
            >
              <a href={mailtoHref}><LifeBuoy className="h-3.5 w-3.5 mr-1.5" />Contact Support</a>
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="submission-uncertain-close-btn">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook that centralizes Kaharian v3.0 studio error handling.
 *
 *   const { handleStudioError, SubmissionUncertainDialog } = useStudioErrorHandler();
 *   try { ... } catch (err) { handleStudioError(err, { category: 'MUSIC', idempotencyKey }); }
 *   // render <SubmissionUncertainDialog /> once in your component tree
 */
export function useStudioErrorHandler() {
  const [uncertainCtx, setUncertainCtx] = useState(null);

  const handleStudioError = useCallback((err, ctx = {}) => {
    const detail = err?.response?.data?.detail;
    const isUncertain =
      err?.response?.status === 202 &&
      detail && typeof detail === 'object' &&
      detail.status === 'submission_uncertain';

    if (isUncertain) {
      setUncertainCtx({
        category: ctx.category || '',
        idempotencyKey: ctx.idempotencyKey || '',
        message: detail.message || '',
        timestamp: new Date().toISOString(),
      });
      return { handled: 'submission_uncertain' };
    }

    // v3.2: structured <CATEGORY>_PROVIDERS_UNAVAILABLE — surface the Hub's safe message
    // (no provider names leaked) with a reassuring "No credits charged" note.
    const isProvidersDown =
      err?.response?.status === 503 &&
      detail && typeof detail === 'object' &&
      typeof detail.code === 'string' &&
      detail.code.endsWith('_PROVIDERS_UNAVAILABLE');
    if (isProvidersDown) {
      toast.error(detail.message || 'Temporarily unavailable — no credits were charged.', {
        duration: 8000,
        id: `providers-down-${detail.category || 'generic'}`,
      });
      return { handled: 'providers_unavailable' };
    }

    const fallback = typeof detail === 'string'
      ? detail
      : (detail?.message || err?.message || ctx.fallbackMessage || 'Generation failed');
    toast.error(fallback);
    return { handled: 'toast' };
  }, []);

  const SubmissionUncertainDialog = () => (
    <SubmissionUncertainModal
      open={!!uncertainCtx}
      context={uncertainCtx}
      onClose={() => setUncertainCtx(null)}
    />
  );

  return { handleStudioError, SubmissionUncertainDialog };
}
